import React, { useState } from "react";
import { Button, CustomDropdown, Modal, TextAreaField } from "../../../components/ui";

/**
 * The reasons a submitted form usually goes back.
 *
 * A list rather than a free box alone, because the reason travels to the client
 * as-written and a hurried practitioner types "wrong" — which tells them
 * nothing about what to change. Every one of these names something the client
 * can act on without a second message.
 *
 * Not exhaustive, which is what the note under it is for.
 */
const REASONS = [
  "Details look incomplete",
  "Height, weight or target weight looks wrong",
  "Medical conditions or medications need more detail",
  "Food preferences need more detail",
  "Answers do not match what we discussed",
  "Filled in by the wrong person",
];

const OPTIONS = REASONS.map((reason) => ({ value: reason, label: reason }));

export interface RejectPreconsultationModalProps {
  /** Whose form is going back, by first name. */
  firstName: string;
  busy: boolean;
  /** Called with the whole of what the client will be told. */
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Sends a submitted form back to the client, with what to fix.
 *
 * The reason is required and the note is not. The backend takes one string, so
 * the two are joined on the way out — the chosen line first, then whatever was
 * added — which keeps the common case one press and still lets somebody say
 * exactly which answer was wrong.
 *
 * Nothing is deleted by this. The answers stay on the form and the client's
 * record is left as it was; a rejection is the practitioner saying these are
 * not right yet.
 */
const RejectPreconsultationModal: React.FC<RejectPreconsultationModalProps> = ({
  firstName,
  busy,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  // Only after a press: a dropdown that goes red before it has been touched is
  // telling somebody off for not having answered yet.
  const [attempted, setAttempted] = useState(false);

  const handleConfirm = () => {
    if (!reason) {
      setAttempted(true);
      return;
    }

    const extra = note.trim();
    onConfirm(extra ? `${reason} - ${extra}` : reason);
  };

  return (
    <Modal
      open
      title="Reject this submission?"
      onClose={onClose}
      dismissOnBackdrop={false}
      className="max-w-[500px]"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            size="md"
            variant="secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            size="md"
            variant="danger"
            onClick={handleConfirm}
            loading={busy}
          >
            Reject submission
          </Button>
        </div>
      }
    >
      <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
        Let {firstName} know what to fix. This won&rsquo;t delete anything
        &mdash; it just marks the form rejected.
      </p>

      <div className="mt-4">
        <p className="mb-1.5 text-[14px] 3xl:text-[15px] text-ink">
          Common reasons
        </p>
        <CustomDropdown
          options={OPTIONS}
          value={reason}
          onChange={(value) => {
            setReason(value);
            setAttempted(false);
          }}
          placeholder="Select reason"
          disabled={busy}
        />
        {attempted && !reason && (
          <p className="mt-1.5 text-[13px] text-[#FF3B30]">
            Pick what {firstName} needs to fix.
          </p>
        )}
      </div>

      <TextAreaField
        className="mt-4"
        label="Note (optional)"
        value={note}
        onValueChange={setNote}
        placeholder={`Details look incomplete - please add your current medication and re-submit.`}
        rows={4}
        disabled={busy}
      />
    </Modal>
  );
};

export default RejectPreconsultationModal;
